import apiClient, { isAuthenticated } from "./client";
import { getCredoraMenuCatalog } from "../constants/credoraMenuCatalog";
import {
  applyMenuAccessPayload,
  clearRemoteMenuAccess,
  setRemoteMenuLinkGrants,
} from "../constants/rbacAccess";

export async function fetchMenuAccess() {
  const { data } = await apiClient.get("/rbac/menu-access");
  applyMenuAccessPayload(data);
  return data;
}

export async function patchMenuAccess(payload) {
  const { data } = await apiClient.patch("/rbac/menu-access", payload);
  applyMenuAccessPayload(data);
  return data;
}

export async function fetchRoles(includeInactive = false) {
  const { data } = await apiClient.get("/rbac/roles", {
    params: { includeInactive },
  });
  return data;
}

export async function createRole(payload) {
  const { data } = await apiClient.post("/rbac/roles", payload);
  return data;
}

export async function updateRole(roleId, payload) {
  const { data } = await apiClient.patch(`/rbac/roles/${roleId}`, payload);
  return data;
}

export async function deleteRole(roleId) {
  await apiClient.delete(`/rbac/roles/${roleId}`);
}

/** Local catalog from routes + sidebar (source of truth). */
export function getLocalMenuLinkCatalog() {
  return getCredoraMenuCatalog();
}

export async function fetchMenuLinkCatalog() {
  try {
    const { data } = await apiClient.get("/rbac/menu-links/catalog");
    if (Array.isArray(data) && data.length > 0) {
      return data;
    }
  } catch {
    /* use local routes/menu catalog */
  }
  return getLocalMenuLinkCatalog();
}

/** Push route-derived catalog to API so grants validate against real app links. */
export async function syncMenuLinkCatalog(items = getLocalMenuLinkCatalog()) {
  const payload = {
    items: items.map((item) => ({
      pathKey: item.pathKey,
      title: item.title,
      section: item.section,
      requiredPermissions: item.requiredPermissions || [],
      inMenu: item.inMenu,
      inRoutes: item.inRoutes,
    })),
  };
  const { data } = await apiClient.put("/rbac/menu-links/catalog", payload);
  return data;
}

export async function fetchMenuLinkGrants(params = {}) {
  const { data } = await apiClient.get("/rbac/menu-links", { params });
  setRemoteMenuLinkGrants(data.items || []);
  return data;
}

export async function createMenuLinkGrant(payload) {
  const { data } = await apiClient.post("/rbac/menu-links", payload);
  await fetchMenuLinkGrants();
  return data;
}

export async function updateMenuLinkGrant(grantId, payload) {
  const { data } = await apiClient.patch(`/rbac/menu-links/${grantId}`, payload);
  await fetchMenuLinkGrants();
  return data;
}

export async function deleteMenuLinkGrant(grantId) {
  await apiClient.delete(`/rbac/menu-links/${grantId}`);
  await fetchMenuLinkGrants();
}

/** Load persisted RBAC when a session exists; ignores 403 for unprivileged users. */
export async function loadMenuAccessIfAuthenticated() {
  if (!isAuthenticated()) {
    clearRemoteMenuAccess();
    return null;
  }
  try {
    const access = await fetchMenuAccess();
    try {
      await fetchMenuLinkGrants();
    } catch (grantErr) {
      if (grantErr.response?.status === 401) {
        setRemoteMenuLinkGrants([]);
      }
      /* keep prior grants on transient errors; 403 = cannot list, not fatal */
    }
    if (isAuthenticated()) {
      try {
        const user = JSON.parse(localStorage.getItem("credora_user") || "null");
        if (user?.role === "super_admin") {
          await syncMenuLinkCatalog();
        }
      } catch {
        /* non-blocking */
      }
    }
    return access;
  } catch (err) {
    if (err.response?.status === 401) {
      clearRemoteMenuAccess();
    }
    return null;
  }
}
